import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";
import DateInput from "@/components/DateInput";
import { downloadCsv } from "@/components/DateScopePicker";

const localTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

const COLUMNS = [
  { header: "Shift date", value: "shift_date" },
  { header: "Area", value: "location_name" },
  { header: "Shift", value: "shift" },
  { header: "Expected", value: "expected" },
  { header: "Counted", value: "counted" },
  { header: "Match", value: (r) => (r.mismatch ? "No" : "Yes") },
  { header: "Missing knives", value: (r) => (r.missing || []).join(", ") },
  { header: "Note", value: "note" },
  { header: "Counted by", value: "counted_by_name" },
  { header: "Counted at", value: "counted_at" },
  { header: "Signed off", value: (r) => (r.signed_off ? "Yes" : r.mismatch ? "PENDING" : "") },
  { header: "Signed off by", value: "signed_off_by_name" },
  { header: "Signed off at", value: "signed_off_at" },
  { header: "Sign-off note", value: "signoff_note" },
];

export default function KnifeHistory({ refreshKey }) {
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/knives/counts", { params: { from_date: from || undefined, to_date: to || undefined } });
    setRows(data);
  }, [from, to]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Panel
      title={`History (${rows.length})`}
      action={
        <Button variant="outline" className="h-10 gap-1.5" disabled={!rows.length} data-testid="knife-export-csv"
          onClick={() => downloadCsv(`knife-counts-${from || "all"}_to_${to || "today"}.csv`, rows, COLUMNS)}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      }
    >
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-44"><div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">From</div><DateInput className="h-11" value={from} onChange={setFrom} dataTestid="knife-history-from" /></div>
        <div className="w-44"><div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">To</div><DateInput className="h-11" value={to} onChange={setTo} dataTestid="knife-history-to" /></div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-stone-500 py-6 text-center" data-testid="knife-history-empty">No knife counts in this range.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="knife-history-table">
            <thead className="text-left text-[11px] uppercase tracking-wider text-stone-500">
              <tr><th className="py-2">Date</th><th>Area</th><th>Shift</th><th className="text-right pr-4">Count</th><th>Note</th><th>By</th><th>Sign-off</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t border-border align-top ${r.mismatch ? "bg-rose-50/70 dark:bg-rose-950/20" : ""}`} data-testid={`knife-history-${r.id}`}>
                  <td className="py-2 tnum whitespace-nowrap">{r.shift_date}</td>
                  <td>{r.location_name}</td>
                  <td className="capitalize">{r.shift}</td>
                  <td className={`text-right pr-4 tnum font-semibold ${r.mismatch ? "text-rose-700" : "text-emerald-700"}`}>{r.counted}/{r.expected}</td>
                  <td className="text-xs max-w-xs">{r.missing?.length > 0 && <div className="font-semibold text-rose-700">Missing: <span className="font-mono">{r.missing.join(", ")}</span></div>}{r.note || (r.missing?.length ? "" : "—")}</td>
                  <td className="text-xs whitespace-nowrap">{r.counted_by_name} · {localTime(r.counted_at)}</td>
                  <td className="text-xs">{r.signed_off ? r.signed_off_by_name : r.mismatch ? <span className="text-rose-700 font-semibold">Pending</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
